import React, { useState, useEffect, useMemo, SyntheticEvent } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import translations from './i18n';
import { PlantLayoutEditorProps } from './plant-layout-editor';
import help from './plant-layout-editor.help';
import { apiUriConsumableDataHttp, apiUriMasterDataHttp } from '../../services/conf/apiConf';
import { fetchAllDataGet_byApiName } from '../../services/gets/getdata';
import { sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import { headers_custom_func_set_logoff } from '../../services/Auth/get_set_AuthHeaders';

import plantLayoutColumns from '../../types/plantLayoutDataTypeCustom';
import { all } from '@plasma/ui.packages.all';
import { use } from 'i18next';
import { values } from 'mobx';


function usePlantLayoutEditorHook(props: PlantLayoutEditorProps) {

  const [blankData, setBlankData] = useState([]);

  const [dataForColumns, setdataForColumns] = useState([]);
  const [dataForModal, setDataForModal] = useState({});
  const [columnNames,setcolumnNames] = useState(plantLayoutColumns());
  const [AllOptions, setAlloptions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [ResetselectedIndex, setResetSelectedIndex] = useState(false);
  const [selectedConsumable, setselectedConsumable] = useState("");
  const [selectedConsumableDesc, setselectedConsumableDesc] = useState("")
  const [uniqueConsumableOptions_list, setuniqueConsumableOptions_list] = useState([]);
  const [uniqueConsumableCodes_list, setuniqueConsumableCodes_list] = useState([]);
  const [consumableSelectionDisabled, setconsumableSelectionDisabled] = useState(true);
  const [unassignedConsumables, setUnassignedComsumables] = useState([]);
  const [isLoadingData, setIsLaodingData] = useState(true);
  const [selectionQtys, setselectionQtys] = useState({quantityredressed: null, quantityinstalled: null});

  const[finalColumns, setFinalColumns] = useState([])


  const [hiddenColumns] = useState(['displayseq', 'drawingnumber', 'isactive', 'remarks','positionnumber','quantityredressed',  'quantityinstalled']);
  const [stillHiddenColumns] = useState([ 'quantityinstalled', 'positionnumber', 'displayseq', 'quantityredressed', 'drawingnumber']);

  useResourceBundle(translations, undefined, 'ef27979e-ad5e-4a6b-bca9-0b7bd86de4e5');
  useHelpBundle(help);

  useEffect(() => {
    // GetPlantLayout is an expensive, auth-gated call - if there's no token it's pointless to
    // fire it. But login itself is async (Keycloak SSO check -> UserLoggedIn POST -> token
    // written to sessionStorage), so a missing token on the very first check can just mean this
    // page mounted a beat before that finished - not that the session is actually stale. Give it
    // a brief grace period to show up before concluding it's genuinely missing and forcing a
    // logout; this avoids interrupting a login that's still in progress.
    let cancelled = false;

    const ensureTokenThenLoad = async () => {
      const maxAttempts = 5;
      const retryDelayMs = 300;

      for (let attempt = 0; attempt < maxAttempts && !sessionStorage.getItem('token'); attempt++) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
      }

      if (cancelled) {
        return;
      }

      if (!sessionStorage.getItem('token')) {
        headers_custom_func_set_logoff();
        window.location.href = '/';
        return;
      }

      loadData();
      loadOptions();
    };

    ensureTokenThenLoad();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadOptions = () =>{
    //console.log("FetchingPLant Data");
    //fetch options only after the fetch of the plant data is completed.
    fetchAllDataGet_byApiName("GetPlantConfigData", apiUriMasterDataHttp()).then((dataFromApi_options) => {
      setAlloptions(dataFromApi_options);
    }).catch((error)=>{
      //console.log("Failed to Fetch plant configs options");
    });

    //fetch options only after the fetch of the plant data is completed.
    fetchAllDataGet_byApiName("GetUnassignedConsumables", apiUriConsumableDataHttp()).then((dataFromApi_options) => {
      setUnassignedComsumables(dataFromApi_options);
    }).catch((error)=>{
      //console.log("Failed to Fetch GetUnassignedConsumables");
    });
  }

  const loadData = () => {
    fetchAllDataGet_byApiName("GetPlantLayout", apiUriMasterDataHttp()).then((dataFromApi) => {

        const formattedDataFromApi = dataFromApi.map((eachData) => {
          const consumableCodesExt = [];
          const consumableCodesExt2 = [];
          const consumablecodeextDetails_ext = []
          const consumableDescToShow = eachData?.consumables?.map((eachConsumable) => {
            consumableCodesExt.push(eachConsumable.consumablecodeext);
            consumableCodesExt2.push(eachConsumable.consumablecodeext + " || " + eachConsumable.billetid);
            const consumablecodeextDetails = {
              consumablecodeext: eachConsumable.consumablecodeext,
              quantityredressed: eachConsumable.quantityredressed,
              quantityinstalled: eachConsumable.quantityinstalled,
            }
            consumablecodeextDetails_ext.push(consumablecodeextDetails);
            return eachConsumable.consumabledescription;
          });
          return({
            displayseq: eachData.displayseq,
            areacode: eachData.areacode,
            zonecode: eachData.zonecode,
            equipmentcode: eachData.equipmentcode,
            drawingnumber: eachData.drawingnumber,
            consumablecode: consumableDescToShow && consumableDescToShow.length > 0 ? consumableDescToShow[0] : '',
            consumablecodeext: consumableCodesExt,
            consumablecodeext2: consumableCodesExt2,
            consumablecodeextDetails: consumablecodeextDetails_ext
          });
        })
        //console.log("Setting data for columns");
        setdataForColumns(formattedDataFromApi);
        setResetSelectedIndex(true);

      }).catch((error)=>{
        //console.log(error);
      })
      .finally(() => setIsLaodingData(false))
  }

  useEffect(() => {
    if (AllOptions) {

      if(!uniqueConsumableOptions_list.length){
        //console.log("creating uniqueConsumableOptions_list")
        const unassignedConsumables_loc = Array.from(new Set((unassignedConsumables || []).map(oneItem => oneItem.consumabledescription)));

        const finalModalOptions = unassignedConsumables_loc?.map(uniqueValue => ({
          display: uniqueValue,
          value: uniqueValue,
        }));
        setuniqueConsumableOptions_list(finalModalOptions);
      }

      //console.log("Creating Options");
      const allColumnsWithOptions = columnNames.map((oneColumn) => {

        switch (oneColumn.accessor) {
          case "consumablecode":
            oneColumn.options = Array.from(new Set((AllOptions?.consumableMaster || []).map(oneItem => oneItem.consumabledescription)));
            oneColumn.options = oneColumn.options.map(uniqueValue => ({
              display: uniqueValue,
              value: uniqueValue,
            }));


            oneColumn.editType = null;
            break;

          case 'zonecode':
            oneColumn.options = (AllOptions?.zoneMaster || []).map((oneItem) => ({
              display: oneItem.zonedesc,
              value: oneItem.zonecode
            }));
            break;

          case 'areacode':
            oneColumn.options = (AllOptions?.areaMaster || []).map(oneItem => ({
              display: oneItem.areadesc,
              value: oneItem.areacode
            }));
            break;

          case 'equipmentcode':
            oneColumn.options = (AllOptions?.equipmentMaster || []).map((oneItem) => ({
              display: oneItem.equipmentdescription,
              value: oneItem.equipmentcode
            }));
            break;

          default:
            break;
        }
        return oneColumn;
      });
      setFinalColumns(allColumnsWithOptions);
    }
  }, [AllOptions, dataForColumns, columnNames, unassignedConsumables]);

  useEffect(() =>{
  //console.log("Loading Modal");
    if(dataForModal){
      setselectedConsumableDesc("");
      const value =  selectedConsumable === "" ? dataForModal.consumablecode : selectedConsumable;
      const filteredCodesExt =  AllOptions.consumableMaster?.filter((item) => item.consumabledescription === value);

      const filteredCodesExtoptionsList = filteredCodesExt?.map((item) => {
          return {
          display: item.consumablecodeext + " || " + item.billetid,
          value: item.consumablecodeext
        }
      } );
      const filteredFromSlections = filteredCodesExtoptionsList?.filter((item) => !dataForModal.consumablecodeext?.includes(item.value.trim()));
      setuniqueConsumableCodes_list(filteredFromSlections);

      if (dataForModal.consumablecode?.trim() === ""){
        //console.log("blank consumable code");
        setconsumableSelectionDisabled(false);
      }
      else if(dataForModal.consumablecodeext?.length < 1){
        //console.log("nothing is attached");
        setconsumableSelectionDisabled(false);
      }
    }
    else{
      setconsumableSelectionDisabled(true);
    }

  },[dataForModal, selectedConsumable, AllOptions]);

  const handleAddConsumable = () => {

    if (selectedConsumableDesc) {
      const newConsumableCode = selectedConsumableDesc?.split("||")[0]?.trim();
      const consumablecodeextDetails = {
        consumablecodeext: newConsumableCode,
        quantityredressed: selectionQtys.quantityredressed,
        quantityinstalled: selectionQtys.quantityinstalled,
      }
      // Use the functional form of setDataForModal to ensure the latest state
      setDataForModal((prevDataForModal) => ({
        ...prevDataForModal,
        consumablecodeext: [...prevDataForModal.consumablecodeext, newConsumableCode],
        consumablecodeextDetails: [...prevDataForModal.consumablecodeextDetails, consumablecodeextDetails]
      }));
      setselectionQtys({quantityredressed: null, quantityinstalled: null});
      setselectedConsumableDesc(undefined);
    }

    //setselectedConsumableDesc("");
  };

  const removeItemFromAddedList = (item) => {
    if (dataForModal) {
      const updatedList = dataForModal.consumablecodeext.filter((existingItem) => existingItem !== item);
      const updatedListDetails = dataForModal.consumablecodeextDetails.filter((existingitem) => existingitem.consumablecodeext !== item);

      setDataForModal({
        ...dataForModal,
        consumablecodeext: updatedList,
        consumablecodeextDetails: updatedListDetails,
      });

      setselectedConsumable(dataForModal.consumablecode)
    }
  };

  const handleSaveFromTable = (data: any) => {
    sendAllDataPost_byApiNameAndData('EditPlantLayout', data, apiUriMasterDataHttp()).then((response) => {
      if(response){
          loadData();
      }
    }).catch((error) => {
      //console.log(error);
    });
  }

  const saveOneRowModalFromModal = (dataFromModal) => {
    const dataToSave_consumablecodeext = dataFromModal.consumablecodeext?.map((item) => {
      const oneOptionProbably = AllOptions.consumableMaster.filter((item_internal) => item_internal.consumablecodeext === item.trim());
      const oneDetailsTechnically = dataForModal.consumablecodeextDetails?.filter((item_details) => item_details.consumablecodeext === item.trim());
      //console.log(oneOptionProbably);
      return {
        displayseq: oneOptionProbably[0].displayseq,
        consumablecodeext: item,
        consumabledescription: oneOptionProbably[0].consumabledescription,
        billetid: oneOptionProbably[0]?.billetid,
        quantityinstalled: Number(oneDetailsTechnically[0].quantityinstalled) || 0,
        quantityredressed: Number(oneDetailsTechnically[0].quantityredressed) || 0,
      };

    });

    const fimalDataTosave = {
      displayseq: dataFromModal.displayseq,
      areacode: dataFromModal.areacode,
      zonecode: dataFromModal.zonecode,
      equipmentcode: dataFromModal.equipmentcode,
      drawingnumber: dataFromModal.drawingnumber,
      consumables: dataToSave_consumablecodeext,
    }

    setselectedConsumable("");
    setselectedConsumableDesc("");
    setDataForModal({});
    sendAllDataPost_byApiNameAndData('AttachConsumablesToLine', fimalDataTosave, apiUriConsumableDataHttp())
      .then((response) =>{ setIsLaodingData(true)})
      .catch((error) => {
        //console.log("failed to save", error);
      })
      .finally(()=>{ loadData();
        setconsumableSelectionDisabled(true);});
  }

  const handleConsumableCodeChange = (value: string) => {
    const selectedConsumableQuantityRedressed = AllOptions?.consumableMaster.filter((item) => item.consumablecodeext === value.split(" || ")[0]);
    setselectionQtys({quantityredressed: selectedConsumableQuantityRedressed[0].quantityredressed, quantityinstalled: selectedConsumableQuantityRedressed[0].quantityinstalled});
    setselectedConsumableDesc(value);
  }


  const selectedConsumableCodeRedressedQtyChange = (event: SyntheticEvent, type: string) => {

    setselectionQtys(prevData => {
      if (type === 'Redressed') {
        return { ...prevData, quantityredressed: event.target.value };
      }
      if (type === 'Installed') {
        return { ...prevData, quantityinstalled: event.target.value };
      }
      return prevData;
    });
  };

  const handleSave = (newData: Array<{ [key: string]: any }>) => {

    let initialDisplaySeq = 0; // starting from 0

    const dataToSave = newData.map((oneItem) => {
      initialDisplaySeq = initialDisplaySeq + 1 // add 1 before saving
      oneItem.displayseq = initialDisplaySeq;
      oneItem.isactive = oneItem.isactive === 'true';
      oneItem.consumables = null;
      return oneItem;
    });

    //console.log(dataToSave);
    handleSaveFromTable(dataToSave);
  };



  const handleSelectionFromTable = (selectionIndex: any, rowData: any) => {
    //console.log(rowData);
    setIsLaodingData(true);
    setResetSelectedIndex(false);
    setShowModal(!showModal);
    setDataForModal(rowData);
  }



  const HandleModalCancel = () => {
    setShowModal(false);
    setResetSelectedIndex(true);
    setselectedConsumable("");
    setselectedConsumableDesc("");
    setDataForModal({});
    setconsumableSelectionDisabled(true);

  }

  const handleModalOk = (dataFromModal) => {
    setShowModal(false);
    saveOneRowModalFromModal(dataFromModal)
  }





  const { t } = useTranslation();

  return {
    t,
    hiddenColumns,
    dataForColumns,
    finalColumns,
    columnNames,
    blankData,
    stillHiddenColumns,
    setBlankData,
    handleSaveFromTable,
    showModal,
    setShowModal,
    ResetselectedIndex,
    setResetSelectedIndex,
    uniqueConsumableOptions_list,
    selectedConsumable,
    setselectedConsumable,
    dataForModal, setDataForModal,
    uniqueConsumableCodes_list,
    selectedConsumableDesc,
    setselectedConsumableDesc,
    saveOneRowModalFromModal,
    setuniqueConsumableCodes_list,
    setconsumableSelectionDisabled,
    handleAddConsumable,
    consumableSelectionDisabled,
    isLoadingData,
    setIsLaodingData,
    handleConsumableCodeChange,
    selectionQtys,
    removeItemFromAddedList,
    selectedConsumableCodeRedressedQtyChange,
    handleModalOk,
    HandleModalCancel,
    handleSelectionFromTable,
    handleSave
  };
}

export default usePlantLayoutEditorHook;
